import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicGuests } from '@/services/guests.api';
import type { FrontendGuest } from '@/services/transform';
import RichTextRenderer from '@/components/base/RichTextRenderer';

interface GuestModalProps {
  guest: FrontendGuest;
  onClose: () => void;
  isZh: boolean;
}

function GuestModal({ guest, onClose, isZh }: GuestModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Poster Container */}
      <div className="relative w-full max-w-6xl max-h-[90vh] md:max-h-[85vh] h-[95vh] md:h-[85vh] overflow-hidden rounded-3xl shadow-2xl bg-white flex flex-col md:flex-row">
        
        {/* Left: Photo Area */}
        <div className="w-full md:w-2/5 h-48 md:h-full bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#2C7DA0] relative flex-shrink-0">
          <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
            {guest.photo ? (
              <img
                src={guest.photo}
                alt={isZh ? guest.name : guest.nameEn}
                className="max-w-full max-h-full object-contain object-center"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))' }}
              />
            ) : (
              <div className="text-center">
                <div className="w-24 h-24 md:w-32 md:h-32 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-3 md:mb-4">
                  <i className="ri-user-line text-4xl md:text-5xl text-white/30" />
                </div>
                <p className="text-white/40 text-sm">{isZh ? guest.name : guest.nameEn}</p>
              </div>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </div>

        {/* Right: Content Area */}
        <div className="w-full md:w-3/5 bg-white flex flex-col flex-1 min-w-0 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#0A2540] to-[#2C7DA0] flex-shrink-0" />
          
          <div className="flex-1 overflow-y-auto p-5 md:p-7">
            {/* Basic Info */}
            <div className="mb-5">
              <h2 className="text-xl md:text-3xl font-bold text-[#0A2540] mb-1.5 leading-tight">
                {isZh ? guest.name : guest.nameEn}
              </h2>
              <p className="text-[#2C7DA0] text-base md:text-lg font-medium mb-1">
                {isZh ? guest.title : guest.titleEn}
              </p>
              <p className="text-[#0A2540]/60 text-sm md:text-base mb-3">
                {isZh ? guest.institution : guest.institutionEn}
              </p>
              
              {/* Quick Info Tags */}
              <div className="flex flex-wrap gap-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F7F9FC] text-[#0A2540]/70 text-xs">
                  <i className="ri-map-pin-line text-[#2C7DA0]" />
                  <span>{isZh ? guest.country : guest.countryEn}</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs">
                  <i className="ri-calendar-line" />
                  <span>{guest.year}</span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 bg-gradient-to-r from-[#2C7DA0]/30 to-transparent" />
              <div className="w-2 h-2 rounded-full bg-[#2C7DA0]/50" />
              <div className="h-px flex-1 bg-gradient-to-l from-[#2C7DA0]/30 to-transparent" />
            </div>

            {/* Expertise */}
            <div className="mb-5">
              <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                  <i className="ri-focus-3-line text-[#2C7DA0] text-xs" />
                </div>
                <span>{t('guestsPage.modal.expertise')}</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {(isZh ? guest.expertise : guest.expertiseEn).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-[#2C7DA0]/10 text-[#2C7DA0] rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div className="mb-5">
              <h3 className="text-[#0A2540] font-bold text-xs md:text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                  <i className="ri-user-3-line text-[#2C7DA0] text-xs" />
                </div>
                <span>{t('guestsPage.modal.bio')}</span>
              </h3>
              <div className="bg-[#F7F9FC] rounded-xl p-4 md:p-5 max-h-[250px] md:max-h-[350px] overflow-y-auto">
                <RichTextRenderer
                  content={isZh ? guest.bio : guest.bioEn}
                  className="text-[#0A2540]/70 text-sm leading-relaxed"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center pt-4 border-t border-[#0A2540]/8">
              {guest.linkedin && (
                <a
                  href={guest.linkedin}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg text-sm font-medium transition-all group"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-linkedin-fill" />
                  </div>
                  <span>{isZh ? '查看 LinkedIn' : 'View LinkedIn'}</span>
                  <div className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-external-link-line text-xs" />
                  </div>
                </a>
              )}
            </div>
          </div>
          
          <div className="h-1 bg-gradient-to-r from-[#2C7DA0] to-[#0A2540] flex-shrink-0" />
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all backdrop-blur-sm cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {/* Decorative Corners */}
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-[#2C7DA0]/10 to-transparent rounded-tr-full" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#7EC8E3]/10 to-transparent rounded-tl-full" />
      </div>
    </div>
  );
}

export default function GuestsCarousel() {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<FrontendGuest | null>(null);
  const [guests, setGuests] = useState<FrontendGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // 加载真实嘉宾数据
  useEffect(() => {
    async function loadGuests() {
      try {
        const data = await getPublicGuests();
        setGuests(data);
      } catch (error) {
        console.error('加载嘉宾数据失败:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGuests();
  }, []);

  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (guests.length === 0) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % guests.length);
    }, 4000);
  }, [guests.length]);

  useEffect(() => {
    startAutoPlay();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [startAutoPlay]);

  const goTo = (idx: number) => {
    setCurrentIndex(idx);
    startAutoPlay();
  };

  const prev = () => {
    setCurrentIndex((p) => (p - 1 + guests.length) % guests.length);
    startAutoPlay();
  };

  const next = () => {
    setCurrentIndex((p) => (p + 1) % guests.length);
    startAutoPlay();
  };

  const visibleIndices = [
    (currentIndex - 1 + guests.length) % guests.length,
    currentIndex,
    (currentIndex + 1) % guests.length,
  ];

  return (
    <section id="guests" className="py-20 md:py-28 bg-[#0A2540] overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-star-line" />
            </div>
            {t('guests.home.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('guests.home.title')}
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('guests.home.subtitle')}
          </p>
        </div>

        {/* Carousel */}
        <div className={`relative transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-white/50">{isZh ? '加载中...' : 'Loading...'}</div>
            </div>
          ) : guests.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-white/50">{isZh ? '暂无嘉宾数据' : 'No guests available'}</div>
            </div>
          ) : (
            <>
              {/* Cards */}
              <div className="hidden md:flex items-center justify-center gap-6">
                {visibleIndices.map((idx, position) => {
                  const guest = guests[idx];
                  const isCenter = position === 1;
                  return (
                    <div
                      key={`carousel-${idx}-${position}-${guest.id}`}
                      className={`bg-white/5 border rounded-2xl p-6 transition-all duration-500 cursor-pointer
                        ${isCenter
                          ? 'border-[#2C7DA0]/50 scale-105 bg-white/10 w-72'
                          : 'border-white/10 scale-95 opacity-60 hover:opacity-80 w-64'
                        }`}
                      onClick={() => isCenter ? setSelectedGuest(guest) : goTo(idx)}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="w-20 h-20 rounded-full overflow-hidden mb-4 ring-2 ring-[#2C7DA0]/30">
                          <img
                            src={guest.photo}
                            alt={isZh ? guest.name : guest.nameEn}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <h3 className="font-bold text-white text-base mb-1">
                          {isZh ? guest.name : guest.nameEn}
                        </h3>
                        <p className="text-[#2C7DA0] text-xs font-medium mb-1">
                          {isZh ? guest.title : guest.titleEn}
                        </p>
                        <p className="text-white/40 text-xs mb-4">
                          {isZh ? guest.institution : guest.institutionEn}
                        </p>
                        {isCenter && (
                          <div className="flex flex-wrap justify-center gap-1 mb-4">
                            {(isZh ? guest.expertise : guest.expertiseEn).slice(0, 2).map((tag, tagIdx) => (
                              <span key={`${guest.id}-carousel-${tagIdx}-${tag}`} className="px-2 py-0.5 bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        {isCenter && (
                          <button
                            onClick={() => setSelectedGuest(guest)}
                            className="text-[#2C7DA0] text-xs font-medium hover:text-[#7EC8E3] transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {t('guests.home.viewProfile')} →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: single card */}
              <div className="md:hidden flex justify-center">
                <div className="bg-white/10 border border-[#2C7DA0]/50 rounded-2xl p-6 w-full max-w-xs">
                  {(() => {
                    const guest = guests[currentIndex];
                    return (
                      <div className="flex flex-col items-center text-center">
                        <div className="w-20 h-20 rounded-full overflow-hidden mb-4 ring-2 ring-[#2C7DA0]/30">
                          <img src={guest.photo} alt={isZh ? guest.name : guest.nameEn} className="w-full h-full object-cover object-top" />
                        </div>
                        <h3 className="font-bold text-white text-base mb-1">{isZh ? guest.name : guest.nameEn}</h3>
                        <p className="text-[#2C7DA0] text-xs font-medium mb-1">{isZh ? guest.title : guest.titleEn}</p>
                        <p className="text-white/40 text-xs mb-4">{isZh ? guest.institution : guest.institutionEn}</p>
                        <button
                          onClick={() => setSelectedGuest(guest)}
                          className="text-[#2C7DA0] text-xs font-medium cursor-pointer whitespace-nowrap"
                        >
                          {t('guests.home.viewProfile')} →
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </>
          )}

          {/* Navigation Arrows */}
          {!loading && guests.length > 0 && (
            <>
              <button
                onClick={prev}
                className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={next}
                className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {guests.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${idx === currentIndex ? 'w-6 h-2 bg-[#2C7DA0]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
            />
          ))}
        </div>


      </div>

      {/* Guest Modal */}
      {selectedGuest && (
        <GuestModal
          guest={selectedGuest}
          onClose={() => setSelectedGuest(null)}
          isZh={isZh}
        />
      )}
    </section>
  );
}
